/** @jsxImportSource theme-ui */

import React, { ChangeEvent, useState } from 'react';
import { ErrorProps } from '../utils/types/error';
import FlexContainer from '../FlexContainer';
import { getVariantOrBase } from '../utils';
import InputWrapper, { ExtractableInputWrapperProps, extractWrapperProps } from '../utils/InputWrapper';
import { StyleOverrideProps } from '../utils/types';
import { defaultShouldDisplayCount } from '../utils/DefaultCountBehavior';

export type InputVariants = 'error' | 'disabled';
export interface NakedInputProps
    extends StyleOverrideProps,
        ErrorProps,
        Omit<React.ComponentPropsWithRef<'input'>, 'css'> {
    variant?: InputVariants;

    /**
     * Very limited usecase. Should mainly be used for adding icons like magnifying glasses.
     */
    leftElement?: React.ReactNode;
}

export interface InputProps extends ExtractableInputWrapperProps, NakedInputProps, Omit<NakedInputProps, 'variant'> {
    shouldDisplayCount?: boolean | ((count: number, maxLength: number) => boolean);
}

export function Input(props: InputProps): JSX.Element {
    const { isError, shouldDisplayCount, onChange, maxLength, count, ...restProps } = props;

    const [currentCount, setCurrentCount] = useState(count);
    const [placeholder, setPlaceholder] = useState(props.placeholder);

    const { extractedProps, otherProps: baseInputProps } = extractWrapperProps<InputProps>(restProps);

    const onChangeWithCountState = (e: ChangeEvent<HTMLInputElement>) => {
        setCurrentCount(e.target.value.length);
        if (onChange) {
            onChange(e);
        }
    };

    const handleFocus = () => {
        setPlaceholder('');
    };
    const handleBlur = () => {
        setPlaceholder(props.placeholder);
    };

    const wrapperProps = {
        displayCount:
            typeof shouldDisplayCount === 'function' ? shouldDisplayCount(currentCount, maxLength) : shouldDisplayCount,
        ...extractedProps,
        isError,
        maxLength,
        count: currentCount,
    };

    const inputProps = {
        ...baseInputProps,
        onChange: onChangeWithCountState,
        maxLength,
        isError,
        onFocus: handleFocus,
        onBlur: handleBlur,
        placeholder,
    };

    return (
        <InputWrapper {...wrapperProps}>
            <NakedInput {...inputProps} />
        </InputWrapper>
    );
}

function calculateVariant({
    variant,
    disabled,
    isError,
}: Pick<NakedInputProps, 'variant' | 'disabled' | 'isError'>): InputVariants {
    if (disabled) {
        return 'disabled';
    }
    if (isError) {
        return 'error';
    }
    return variant;
}

export function NakedInput(props: NakedInputProps): JSX.Element {
    const { sx, isError, leftElement, maxLength, variant: userVariant, ...otherProps } = props;
    const variant = calculateVariant({ isError, variant: userVariant, disabled: props.disabled });

    return (
        <FlexContainer alignItems="center" sx={{ variant: getVariantOrBase('forms.input', variant) }}>
            {leftElement}
            <input
                maxLength={maxLength}
                sx={{
                    ...sx,
                    resize: 'none',
                }}
                onFocus={props.onFocus}
                onBlur={props.onBlur}
                {...otherProps}
            />
        </FlexContainer>
    );
}
Input.defaultProps = {
    shouldDisplayCount: defaultShouldDisplayCount,
    count: 0,
    maxLength: Infinity,
};
